import type { DraftEvent, NostrEvent } from "../types"
import { LocalSigner } from "./LocalSigner"
import { SignerError, type Signer } from "./types"

/** The Google account behind a session (never includes tokens). */
export interface GoogleIdentity {
  sub: string
  email?: string
  name?: string
  picture?: string
}

export interface GoogleIdTokenClaims {
  iss: string
  aud: string | string[]
  sub: string
  email?: string
  email_verified?: string | boolean
  name?: string
  picture?: string
  exp: number
  iat?: number
  nonce?: string
}

const GOOGLE_ISSUERS = new Set(["accounts.google.com", "https://accounts.google.com"])
const GIS_SRC = "https://accounts.google.com/gsi/client"
const GOOGLE_JWKS_URL = "https://www.googleapis.com/oauth2/v3/certs"
const KEY_INFO = "nostr-google-key/v1"
const JWKS_TTL = 60 * 60 * 1000

interface Jwk extends JsonWebKey {
  kid?: string
}

interface GoogleJwks {
  keys: Jwk[]
}

let jwksCache: { fetchedAt: number; value: GoogleJwks } | undefined

function decodeBase64Url(value: string): Uint8Array {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/")
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4)
  let binary: string
  try {
    binary = atob(padded)
  } catch {
    throw new SignerError("Google returned a malformed identity token", "invalid")
  }
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

function decodeJson(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(new TextDecoder().decode(decodeBase64Url(value)))
    if (!parsed || typeof parsed !== "object") throw new Error("not an object")
    return parsed as Record<string, unknown>
  } catch {
    throw new SignerError("Google returned a malformed identity token", "invalid")
  }
}

/** Copies the bytes into a plain ArrayBuffer, which WebCrypto accepts everywhere. */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.length)
  new Uint8Array(buffer).set(bytes)
  return buffer
}

/**
 * Validates the claims of a Google ID token.
 *
 * Signature verification happens separately (`verifyGoogleIdToken`); this pure
 * check keeps the aud/iss/expiry/nonce rules testable.
 */
export function verifyGoogleClaims(
  claims: GoogleIdTokenClaims,
  options: { clientId: string; nonce?: string; nowMs?: number },
): void {
  if (!GOOGLE_ISSUERS.has(claims.iss)) {
    throw new SignerError("The Google token was not issued by Google", "invalid")
  }
  const audiences = Array.isArray(claims.aud) ? claims.aud : [claims.aud]
  if (!audiences.includes(options.clientId)) {
    throw new SignerError("The Google token was issued for a different application", "invalid")
  }
  if (!claims.sub) throw new SignerError("The Google token has no account identifier", "invalid")
  const now = options.nowMs ?? Date.now()
  if (typeof claims.exp !== "number" || claims.exp * 1000 <= now) {
    throw new SignerError("The Google session expired, try again", "unavailable")
  }
  if (options.nonce && claims.nonce !== options.nonce) {
    throw new SignerError("The Google token does not match this sign-in attempt", "invalid")
  }
}

async function fetchJwks(fetchImpl: typeof fetch): Promise<GoogleJwks> {
  if (jwksCache && Date.now() - jwksCache.fetchedAt < JWKS_TTL) return jwksCache.value
  const response = await fetchImpl(GOOGLE_JWKS_URL, { cache: "no-store" })
  if (!response.ok) throw new SignerError("Google public keys could not be loaded", "unavailable")
  const value = (await response.json()) as GoogleJwks
  if (!Array.isArray(value.keys) || value.keys.length === 0) {
    throw new SignerError("Google returned no public keys", "unavailable")
  }
  jwksCache = { fetchedAt: Date.now(), value }
  return value
}

/** Verifies the RS256 signature of a Google ID token against Google's JWKS. */
export async function verifyGoogleIdToken(
  idToken: string,
  options: { clientId: string; nonce?: string; fetchImpl?: typeof fetch; nowMs?: number },
): Promise<GoogleIdTokenClaims> {
  const parts = idToken.split(".")
  if (parts.length !== 3) throw new SignerError("Google returned a malformed identity token", "invalid")
  const [rawHeader, rawPayload, rawSignature] = parts

  const header = decodeJson(rawHeader)
  if (header.alg !== "RS256") throw new SignerError("Unexpected Google token algorithm", "invalid")

  const claims = decodeJson(rawPayload) as unknown as GoogleIdTokenClaims
  verifyGoogleClaims(claims, { clientId: options.clientId, nonce: options.nonce, nowMs: options.nowMs })

  const subtle = globalThis.crypto?.subtle
  if (!subtle) throw new SignerError("This browser cannot verify Google tokens (WebCrypto missing)", "unavailable")

  const jwks = await fetchJwks(options.fetchImpl ?? fetch)
  const jwk = header.kid ? jwks.keys.find((key) => key.kid === header.kid) : jwks.keys[0]
  if (!jwk) throw new SignerError("Google signed this token with an unknown key", "invalid")

  const key = await subtle.importKey("jwk", jwk, { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" }, false, [
    "verify",
  ])
  const valid = await subtle.verify(
    "RSASSA-PKCS1-v1_5",
    key,
    toArrayBuffer(decodeBase64Url(rawSignature)),
    new TextEncoder().encode(`${rawHeader}.${rawPayload}`),
  )
  if (!valid) throw new SignerError("The Google identity token signature is not valid", "invalid")
  return claims
}

/**
 * Derives the Nostr secret key that belongs to one Google account.
 *
 * HKDF(ikm = Google `sub`, salt = client id, info = app string) always yields the
 * same key for the same Google account, which is what makes the login portable
 * across devices without a server. See docs/FEATURES.md for the trust model.
 */
export async function deriveGoogleSecretKey(sub: string, salt: string): Promise<Uint8Array> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) throw new SignerError("This browser cannot derive a key (WebCrypto missing)", "unavailable")
  const material = await subtle.importKey("raw", new TextEncoder().encode(sub), "HKDF", false, ["deriveBits"])
  const bits = await subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new TextEncoder().encode(salt),
      info: new TextEncoder().encode(KEY_INFO),
    },
    material,
    256,
  )
  return new Uint8Array(bits)
}

/** Verifies a Google ID token and returns the derived signing key. */
export async function completeGoogleSignIn(
  idToken: string,
  options: { clientId: string; nonce?: string; fetchImpl?: typeof fetch },
): Promise<{ identity: GoogleIdentity; secretKey: Uint8Array }> {
  const claims = await verifyGoogleIdToken(idToken, options)
  const secretKey = await deriveGoogleSecretKey(claims.sub, `google:${options.clientId}`)
  return {
    identity: {
      sub: claims.sub,
      email: typeof claims.email === "string" ? claims.email : undefined,
      name: typeof claims.name === "string" ? claims.name : undefined,
      picture: typeof claims.picture === "string" ? claims.picture : undefined,
    },
    secretKey,
  }
}

/** Session signer for a Google-derived key. */
export class GoogleSigner implements Signer {
  readonly method = "google" as const
  readonly identity: GoogleIdentity
  private readonly local: LocalSigner

  constructor(secretKey: Uint8Array, identity: GoogleIdentity) {
    this.local = new LocalSigner(secretKey)
    this.identity = identity
  }

  async getPublicKey(): Promise<string> {
    return this.local.getPublicKey()
  }

  async signEvent(draft: DraftEvent): Promise<NostrEvent> {
    return this.local.signEvent(draft)
  }

  async nip04Encrypt(pubkey: string, plaintext: string): Promise<string> {
    return this.local.nip04Encrypt(pubkey, plaintext)
  }

  async nip04Decrypt(pubkey: string, ciphertext: string): Promise<string> {
    return this.local.nip04Decrypt(pubkey, ciphertext)
  }
}

interface GoogleAccountsId {
  initialize(config: Record<string, unknown>): void
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void
  cancel(): void
}

let gisScript: Promise<void> | undefined

/** Loads the Google Identity Services script once. */
function loadGoogleScript(): Promise<void> {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return Promise.reject(new SignerError("Google sign-in needs a browser", "unavailable"))
  }
  if (gisScript) return gisScript
  gisScript = new Promise<void>((resolve, reject) => {
    const fail = () =>
      reject(new SignerError("Google sign-in could not be loaded (blocked by an extension or offline)", "unavailable"))
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`)
    if (existing) {
      if (googleAccountsId()) resolve()
      else {
        existing.addEventListener("load", () => resolve())
        existing.addEventListener("error", fail)
      }
      return
    }
    const script = document.createElement("script")
    script.src = GIS_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      gisScript = undefined
      fail()
    }
    document.head.appendChild(script)
  })
  return gisScript
}

function googleAccountsId(): GoogleAccountsId | undefined {
  const google = (window as unknown as { google?: { accounts?: { id?: GoogleAccountsId } } }).google
  const accountsId = google?.accounts?.id
  return accountsId && typeof accountsId.renderButton === "function" ? accountsId : undefined
}

export interface GoogleButtonOptions {
  clientId: string
  /** Minted per attempt; `completeGoogleSignIn` rejects a token for another nonce. */
  nonce: string
  onCredential: (idToken: string) => void
  width?: number
  text?: "signin_with" | "signup_with" | "continue_with" | "signin"
}

/**
 * Renders Google's own sign-in button (required by their branding rules) and
 * returns a disposer. Credentials arrive through `onCredential`.
 */
export async function renderGoogleButton(
  element: HTMLElement,
  options: GoogleButtonOptions,
): Promise<() => void> {
  await loadGoogleScript()
  const accountsId = googleAccountsId()
  if (!accountsId) throw new SignerError("Google sign-in is unavailable in this browser", "unavailable")

  accountsId.initialize({
    client_id: options.clientId,
    nonce: options.nonce,
    ux_mode: "popup",
    auto_select: false,
    callback: (response: { credential?: string }) => {
      if (response?.credential) options.onCredential(response.credential)
    },
  })
  accountsId.renderButton(element, {
    type: "standard",
    theme: "outline",
    size: "large",
    shape: "rectangular",
    text: options.text ?? "continue_with",
    logo_alignment: "left",
    width: options.width ?? 260,
  })
  return () => {
    try {
      accountsId.cancel()
    } catch {
      // the widget may already be gone
    }
    element.replaceChildren()
  }
}
